import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { WButton } from '@wareclouds/wareclouds-ui';

import {
  CreateAccountStepsIndicator,
  OnboardingPageLayout,
  OnboardingSectionMenuList,
} from '../../../shared';
import { useTrainingMenu } from '../../hooks';
import {
  getStartModalShown,
  saveStartModalShown,
} from '@/features/onboarding/training/services';
import { TrainingResultModal, TrainingStartModal } from '../components';

export default function TrainingMenuPage() {
  const { t } = useTranslation(['onboarding', 'button']);
  const navigate = useNavigate();
  const [showStartModal, setShowStartModal] = useState(false);

  const {
    menuItems,
    allCompleted,
    approvalRatePercentage,
    submitResult,
    handleSubmit,
    handleRetry,
    handlePassedModalContinue,
    isSubmittingCertified,
  } = useTrainingMenu();

  useEffect(() => {
    if (!getStartModalShown()) {
      setShowStartModal(true);
    }
  }, []);

  const handleStartModalClose = () => {
    saveStartModalShown();
    setShowStartModal(false);
  };

  const steps = [
    { label: t('steps.profile') },
    { label: t('steps.training') },
    { label: t('steps.registration') },
  ];

  const handleSectionClick = (sectionId) => {
    navigate(`section/${sectionId.replaceAll('_', '-')}`);
  };

  return (
    <OnboardingPageLayout
      footer={
        <WButton
          label={t('submit', { ns: 'button' })}
          variant="contained"
          color="primary"
          fullWidth
          isLoading={isSubmittingCertified}
          isDisabled={
            !allCompleted || isSubmittingCertified || submitResult !== null
          }
          onClick={handleSubmit}
        />
      }
    >
      <CreateAccountStepsIndicator
        steps={steps}
        activeStep={1}
        completedSteps={[0]}
      />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '2rem', lineHeight: 1.2 }}>🚀</Typography>
        <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5 }}>
          {t('training.menu.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {t('training.menu.description', { approvalRatePercentage })}
        </Typography>
      </Box>

      <OnboardingSectionMenuList
        items={menuItems}
        onItemClick={handleSectionClick}
      />

      <TrainingResultModal
        open={submitResult !== null}
        result={submitResult}
        approvalRatePercentage={approvalRatePercentage}
        onAction={
          submitResult === 'passed' ? handlePassedModalContinue : handleRetry
        }
      />

      <TrainingStartModal
        open={showStartModal}
        onStart={handleStartModalClose}
      />
    </OnboardingPageLayout>
  );
}
